import debounce from "lodash.debounce";
import { useCallback, useState } from "react";
import { z } from "zod";

export default function useFormValidation<T>(
  state: T,
  validationSchema: z.ZodType<T>,
  { debounceTime = 300 }: { debounceTime?: number } = {}
) {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateField = useCallback(
    debounce((field: keyof T, value: string | number) => {
      const result = validationSchema.safeParse({
        ...state,
        [field]: value,
      });
      if (result.success) {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
      } else {
        const fieldError = result.error.issues.find(
          (error) => error.path[0] === field
        );
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: fieldError ? fieldError.message : undefined,
        }));
      }
    }, debounceTime),
    [state]
  );

  const safeParse = (data: T) => {
    const result = validationSchema.safeParse(data);
    if (result.success) {
      setErrors({});
    } else {
      const newErrors: Partial<Record<keyof T, string>> = {};
      result.error.issues.forEach((error) => {
        if (error.path.length > 0) {
          newErrors[error.path[0] as keyof T] = error.message;
        }
      });
      setErrors(newErrors);
    }
    return result;
  };

  return {
    errors,
    validateField,
    safeParse,
  };
}
